import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DocStatusBadge } from "@/components/StatusBadge";
import { DisclosureClassBadge, DivisionBadge, ExpiryBadge } from "@/components/VaultBadges";
import { toast } from "sonner";
import {
  Upload, Download, Link2, Trash2, Send, History, FileUp, ShieldAlert,
} from "lucide-react";

// Kandake Document Repository — full document detail body. Used inside the
// Vault page sheet. Handles: status/metadata edits, file upload adjoining
// the request status, download, multi-record links, version supersession,
// deliberate furnishing records, furnish log, version history, audit feed.

export function VaultDocumentDetail({ docId, config, canWriteVault, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [linkForm, setLinkForm] = useState({ record_type: "track", record_id: "", label: "" });
  const [tracks, setTracks] = useState([]);
  const [furnishOpen, setFurnishOpen] = useState(false);
  const [furnish, setFurnish] = useState({ recipient: "", purpose: "", authorized: false });

  async function load() {
    try {
      const [d, a] = await Promise.all([
        api.get(`/vault/documents/${docId}`),
        api.get(`/vault/documents/${docId}/audit`).catch(() => ({ data: { events: [] } })),
      ]);
      setDoc(d.data);
      setAuditRows(a.data.events || []);
    } catch (e) { toast.error(errMsg(e)); }
  }

  useEffect(() => { if (docId) load(); /* eslint-disable-next-line */ }, [docId]);
  useEffect(() => {
    api.get("/tracks").then(r => setTracks(r.data || [])).catch(() => {});
  }, []);

  if (!doc) return <div className="py-10 text-center text-sm text-muted-foreground" data-testid="vault-detail-loading">Loading document…</div>;

  const links = (doc.links || []).filter(l => !l.legacy);
  const restricted = doc.disclosure_class === "RESTRICTED";

  async function patch(fields, okMsg = "Updated") {
    setBusy(true);
    try {
      const r = await api.patch(`/vault/documents/${docId}`, fields);
      setDoc(prev => ({ ...prev, ...r.data }));
      toast.success(okMsg);
      onChanged && onChanged();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  }

  async function uploadFile(file, asNewVersion = false) {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const url = asNewVersion
        ? `/vault/documents/${docId}/versions`
        : `/vault/documents/${docId}/file`;
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(asNewVersion ? `New version v${r.data.version} created — previous version superseded` : "File uploaded");
      onChanged && onChanged(asNewVersion ? r.data.id : null);
      if (!asNewVersion) load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  }

  async function download() {
    try {
      const r = await api.get(`/vault/documents/${docId}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_original_filename || `${doc.document_type}.bin`;
      a.click();
      URL.revokeObjectURL(url);
      if (restricted) toast.info("Restricted download recorded in the audit trail");
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function addLink() {
    if (!linkForm.record_id) { toast.error("Record ID required"); return; }
    try {
      await api.post(`/vault/documents/${docId}/links`, linkForm);
      toast.success("Record linked");
      setLinkForm({ record_type: "track", record_id: "", label: "" });
      onChanged && onChanged();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function removeLink(l) {
    try {
      await api.delete(`/vault/documents/${docId}/links`, { params: { record_type: l.record_type, record_id: l.record_id } });
      toast.success("Link removed");
      onChanged && onChanged();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function submitFurnish() {
    try {
      await api.post(`/vault/documents/${docId}/furnish`, furnish);
      toast.success("Furnishing recorded — no email was sent; delivery remains a manual operator action");
      setFurnishOpen(false);
      setFurnish({ recipient: "", purpose: "", authorized: false });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  const trackLabel = (rid) => {
    const t = tracks.find(x => x.id === rid);
    return t ? `${t.track_title} · ${t.track_id}` : rid;
  };

  return (
    <div className="space-y-5" data-testid="vault-document-detail">
      {/* Header badges */}
      <div className="flex flex-wrap items-center gap-2">
        <DisclosureClassBadge value={doc.disclosure_class} />
        <DivisionBadge value={doc.division_scope} />
        <DocStatusBadge status={doc.document_status} />
        {!doc.is_current && (
          <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded doc-archived" data-testid="vault-superseded-flag">
            Superseded — v{doc.version}
          </span>
        )}
        <ExpiryBadge isExpired={doc.is_expired} expirationDate={doc.expiration_date} />
        <span className="text-[11px] font-mono text-muted-foreground ml-auto">v{doc.version} · {doc.id.slice(0, 8)}…</span>
      </div>

      {restricted && (
        <div className="flex items-start gap-2 text-xs rounded border p-2.5" style={{ borderColor: "hsl(347 72% 22% / 0.45)", background: "hsl(347 72% 22% / 0.08)" }} data-testid="vault-restricted-notice">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "hsl(347 65% 62%)" }} />
          <span>Restricted document — never automatically furnished externally. Release requires a specific request and explicit operator authorization. All downloads and furnishings are audited.</span>
        </div>
      )}

      {/* Status / metadata controls */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={doc.document_status} onValueChange={(v) => patch({ document_status: v }, `Status → ${v}`)} disabled={!canWriteVault || busy}>
            <SelectTrigger data-testid="vault-detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(config?.statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              {(config?.legacy_statuses || []).map(s => <SelectItem key={s} value={s}>{s} (legacy)</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Applicability</Label>
          <Select value={doc.applicability} onValueChange={(v) => patch({ applicability: v }, `Applicability → ${v}`)} disabled={!canWriteVault || busy}>
            <SelectTrigger data-testid="vault-detail-applicability-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(config?.applicability_options || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Division scope</Label>
          <Select value={doc.division_scope} onValueChange={(v) => patch({ division_scope: v }, `Division → ${v}`)} disabled={!canWriteVault || busy}>
            <SelectTrigger data-testid="vault-detail-division-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(config?.your_visible_divisions || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Expiration date</Label>
          <Input
            type="date"
            defaultValue={(doc.expiration_date || "").slice(0, 10)}
            onBlur={(e) => { if (e.target.value !== (doc.expiration_date || "").slice(0, 10)) patch({ expiration_date: e.target.value }, "Expiration updated"); }}
            disabled={!canWriteVault}
            data-testid="vault-detail-expiration-input"
          />
        </div>
      </div>

      {/* File — upload control adjoins the request/status per spec §7 */}
      <div className="rounded border border-border p-3 space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">File</div>
        {doc.file_storage_path ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm truncate max-w-[220px]" data-testid="vault-detail-filename">{doc.file_original_filename || doc.file_name}</span>
            <span className="text-[11px] text-muted-foreground">{doc.file_size ? `${Math.round(doc.file_size / 1024)} KB` : ""}</span>
            <Button size="sm" variant="outline" onClick={download} data-testid="vault-detail-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" />Download
            </Button>
            {canWriteVault && doc.is_current && (
              <label className="inline-flex">
                <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0], true)} data-testid="vault-detail-new-version-input" />
                <Button size="sm" variant="outline" asChild>
                  <span className="cursor-pointer"><FileUp className="w-3.5 h-3.5 mr-1.5" />New version</span>
                </Button>
              </label>
            )}
          </div>
        ) : (
          canWriteVault ? (
            <label className="inline-flex" data-testid="vault-detail-upload-label">
              <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0], false)} data-testid="vault-detail-upload-input" />
              <Button size="sm" style={{ background: "hsl(347 72% 22%)" }} asChild>
                <span className="cursor-pointer"><Upload className="w-3.5 h-3.5 mr-1.5" />Upload {doc.document_status === "Requested" ? "requested document" : "file"}</span>
              </Button>
            </label>
          ) : <span className="text-xs text-muted-foreground">No file uploaded.</span>
        )}
      </div>

      {/* Links — one upload, many records (spec §1) */}
      <div className="rounded border border-border p-3 space-y-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" />Linked records</div>
        {links.length === 0 && <div className="text-xs text-muted-foreground">Not linked to any record yet.</div>}
        {links.map((l, i) => (
          <div key={`${l.record_type}-${l.record_id}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`vault-link-row-${l.record_type}-${l.record_id}`}>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground w-[120px] shrink-0">{l.record_type.replace("_", " ")}</span>
            <span className="truncate">{l.record_type === "track" ? trackLabel(l.record_id) : (l.label || l.record_id)}</span>
            {canWriteVault && (
              <Button size="icon" variant="ghost" className="h-7 w-7 ml-auto" onClick={() => removeLink(l)} data-testid={`vault-unlink-${l.record_type}-${l.record_id}`} aria-label="Remove link">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
        {canWriteVault && (
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div className="w-[150px]">
              <Label className="text-[11px] text-muted-foreground">Record type</Label>
              <Select value={linkForm.record_type} onValueChange={(v) => setLinkForm(f => ({ ...f, record_type: v, record_id: "" }))}>
                <SelectTrigger className="h-9" data-testid="vault-link-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(config?.link_record_types || []).map(rt => <SelectItem key={rt} value={rt}>{rt.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {linkForm.record_type === "track" ? (
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[11px] text-muted-foreground">Track</Label>
                <Select value={linkForm.record_id} onValueChange={(v) => setLinkForm(f => ({ ...f, record_id: v }))}>
                  <SelectTrigger className="h-9" data-testid="vault-link-track-select"><SelectValue placeholder="Choose a track…" /></SelectTrigger>
                  <SelectContent>
                    {tracks.map(t => <SelectItem key={t.id} value={t.id}>{t.track_title} · {t.track_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[11px] text-muted-foreground">Record ID / name</Label>
                <Input className="h-9" value={linkForm.record_id} onChange={(e) => setLinkForm(f => ({ ...f, record_id: e.target.value }))} placeholder="e.g. writer name or record id" data-testid="vault-link-id-input" />
              </div>
            )}
            <Button size="sm" variant="outline" onClick={addLink} data-testid="vault-link-add-btn">Link</Button>
          </div>
        )}
      </div>

      {/* Furnishing — deliberate operator record only */}
      <div className="rounded border border-border p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />External furnishing</div>
          <Button size="sm" variant="outline" onClick={() => setFurnishOpen(true)} data-testid="vault-furnish-open-btn">Record furnishing</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Nothing is emailed automatically. This records a deliberate external release with recipient and purpose. {restricted ? "Restricted documents require explicit operator authorization." : ""}</p>
        {(doc.furnish_log || []).length > 0 && (
          <div className="space-y-1.5 pt-1">
            {[...doc.furnish_log].reverse().map(f => (
              <div key={f.id} className="text-xs flex flex-wrap gap-x-2 gap-y-0.5" data-testid={`vault-furnish-entry-${f.id}`}>
                <span className="font-mono text-muted-foreground">{(f.furnished_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="font-medium">→ {f.recipient}</span>
                <span className="text-muted-foreground">· {f.purpose}</span>
                <span className="text-muted-foreground">· by {f.furnished_by}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Version history */}
      {(doc.version_history || []).length > 0 && (
        <div className="rounded border border-border p-3 space-y-1.5">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5"><History className="w-3.5 h-3.5" />Version history</div>
          {doc.version_history.map(v => (
            <div key={v.id} className="text-xs flex gap-2" data-testid={`vault-version-row-${v.id}`}>
              <span className="font-mono">v{v.version}</span>
              <DocStatusBadge status={v.document_status} />
              <span className="text-muted-foreground truncate">{v.file_original_filename || "—"}</span>
              <span className="font-mono text-muted-foreground ml-auto">{(v.updated_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Notes */}
      <div>
        <Label className="text-xs text-muted-foreground">Notes</Label>
        <Textarea
          defaultValue={doc.notes || ""}
          rows={3}
          maxLength={2000}
          disabled={!canWriteVault}
          onBlur={(e) => { if (e.target.value !== (doc.notes || "")) patch({ notes: e.target.value }, "Notes saved"); }}
          data-testid="vault-detail-notes"
        />
      </div>

      <Separator />

      {/* Audit feed */}
      <div className="space-y-1.5">
        <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Audit trail</div>
        {auditRows.length === 0 && <div className="text-xs text-muted-foreground">No audit events yet.</div>}
        {auditRows.slice(0, 30).map((a, i) => (
          <div key={i} className="text-xs flex gap-2" data-testid={`vault-audit-row-${i}`}>
            <span className="font-mono text-muted-foreground whitespace-nowrap">{(a.created_at || a.timestamp || "").slice(0, 16).replace("T", " ")}</span>
            <span className="font-medium whitespace-nowrap">{a.action_type}</span>
            <span className="text-muted-foreground truncate">{a.user_email || a.actor_email || ""}</span>
          </div>
        ))}
      </div>

      {/* Furnish dialog */}
      <Dialog open={furnishOpen} onOpenChange={setFurnishOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record external furnishing</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Recipient <span className="text-destructive">*</span></Label>
              <Input value={furnish.recipient} onChange={(e) => setFurnish(f => ({ ...f, recipient: e.target.value }))} placeholder="e.g. Music supervisor / counsel name or email" data-testid="vault-furnish-recipient" />
            </div>
            <div>
              <Label className="text-xs">Reason / purpose <span className="text-destructive">*</span></Label>
              <Textarea value={furnish.purpose} onChange={(e) => setFurnish(f => ({ ...f, purpose: e.target.value }))} rows={2} placeholder="Why this document is being released" data-testid="vault-furnish-purpose" />
            </div>
            {doc.disclosure_class !== "OPERATING_PROOF" && (
              <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="vault-furnish-authorize-label">
                <Checkbox checked={furnish.authorized} onCheckedChange={(v) => setFurnish(f => ({ ...f, authorized: !!v }))} data-testid="vault-furnish-authorize-checkbox" className="mt-0.5" />
                <span>I am explicitly authorizing release of this <b>{doc.disclosure_class === "RESTRICTED" ? "Restricted" : "Deal Delivery"}</b> document following a specific request.</span>
              </label>
            )}
            <p className="text-[11px] text-muted-foreground">This records the furnishing in the vault and audit trail. It does not send anything.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFurnishOpen(false)} data-testid="vault-furnish-cancel">Cancel</Button>
            <Button onClick={submitFurnish} style={{ background: "hsl(347 72% 22%)" }} data-testid="vault-furnish-submit">Record furnishing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
